import React, { useState, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import { CONST_TREE_RULE } from "./constants";
import { capitalize } from "common/api";
import { buildCommand } from "./utils";
import Table from "common/graphs/Table/Table";
import Modal from "common/Modal";

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const FooterError = styled.div`
  width: 100%;
  text-align: left;
  margin-bottom: 10px;
  margin-left: 5px;
`;

function getUsedProfAndHeader(rules, profs) {
  return profs.reduce((acc, prof) => {
    const profName = prof.attr;
    const ruleWithProf = rules.find((rule) => {
      return rule[profName] !== CONST_TREE_RULE.ANY;
    });
    if (ruleWithProf) {
      acc.push(prof);
    }
    return acc;
  }, []);
}

function getHasConflicts(conflicts, id) {
  if (!conflicts) return "no";
  return conflicts[id] ? "yes" : "no";
}

function isDefaultRule(policy, rule, defaultPolicy) {
  const testRule = { ...rule };
  delete testRule.id;
  delete testRule.poli;
  return (
    policy === defaultPolicy &&
    Object.values(testRule).every((rule) => rule === "any")
  );
}

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  view,
  whenDone,
  defaultPolicy
}) => {
  const itemsToDelete = items.filter((rule) => !isDefaultRule(rule.poli, rule, defaultPolicy));
  const handleApply = () => {
    const cmds = itemsToDelete.map((rule) => buildCommand("clear", rule, view));
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then((response) => {
        if (response.length === 0) {
          setShowDeleteModal(false);
          whenDone();
        } else if (response.substring(0, 5) === "%WARN") {
          showModalInfo("Warning:", response);
          setShowDeleteModal(false);
          whenDone();
        } else {
          showModalError("Error:", response);
        }
      })
      .catch((error) => showModalError(error, ""));
  };

  if (!showDeleteModal) {
    return null;
  }
  const includesDefaultPolicy = items.some((rule) => isDefaultRule(rule.poli, rule, defaultPolicy));
  
  const title =
    itemsToDelete.length === 1 ? `Remove rule?` : `Remove ${itemsToDelete.length} rules?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {itemsToDelete.length === 1 ? <p>WARNING: Rule data will be lost</p> : null}
          {itemsToDelete.length > 1 ? (
            <>
              {itemsToDelete.length < 100 ? (
                <ul>{itemsToDelete.map((rule) => (<li key={rule.id}>{`Rule ${rule.id}`}</li>))}</ul>
              ) : null}
              <p>WARNING: Rules data will be lost</p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
      footerMessage={
        includesDefaultPolicy ? (
          <FooterError className="color-red">The rule with default policy cannot be deleted.</FooterError>
        ) : null
      }
    />
  );
};

const RulesTable = ({
  rules,
  conflicts,
  view,
  profs,
  poli,
  typeProfileMap,
}) => {
  const actions = useContext(ActionsContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);
  const profiles = getUsedProfAndHeader(rules, profs);
  const defaultPolicy = `${view}-default`;
  const viewPage = `viewRules${capitalize(view)}`;

  const profileColumns = profiles.map((profile, profIdx) => {
    return {
      label: profile.header,
      id: profile.header,
      numeric: false,
      cellClassName: (value) =>
        value === CONST_TREE_RULE.ANY ? "" : "hyperlink-text",
      onClick: (_header, row) => {
        const id = row["RULE"];
        const rule = rules.find((rule) => rule.id === id);
        if (rule[profile.attr] === CONST_TREE_RULE.ANY) return;
        typeProfileMap[profile.type](rule[profile.attr], viewPage);
      },
    };
  });

  const rows = rules.map((rule, index) => {
    const hasConflict = getHasConflicts(conflicts, rule.id);
    const profilesEntries = profiles.reduce((acc, profile) => {
      acc[profile.header] = rule[profile.attr];
      return acc;
    }, {});
    return {
      id: rule.id !== undefined ? rule.id : index,
      RULE: rule.id,
      POLICY: rule[poli.attr],
      CONFLICT: hasConflict,
      ...profilesEntries,
    };
  });

  const columns = [
    {
      label: "RULE",
      id: "RULE",
      numeric: false,
      cellClassName: (value, row) => {
        return row["CONFLICT"] === "no" || row["CONFLICT"] === undefined
          ? "col-green"
          : "col-orange";
      },
      parse: value => Number(value) || 0,
    },
    ...profileColumns,
    {
      label: "POLICY",
      id: "POLICY",
      cellClassName: "hyperlink-text",
      onClick: (policy, row) => {
        const id = row["RULE"];
        const rule = rules.find((rule) => rule.id === id);
        if (rule) {
          typeProfileMap.policy(rule.poli, viewPage, { tab: 1 });
        }
      },
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Edit",
          icon: "edit",
          onClick: (row) => {
            const id = row["RULE"];
            const rule = rules.find((rule) => rule.id === id);
            if (rule) {
              actions.send("open-rule-modal", { type: "Edit", rule });
            }
          },
          hide: (row) => {
            const id = row["RULE"];
            const rule = rules.find((rule) => rule.id === id);
            return isDefaultRule(row["POLICY"], rule, defaultPolicy);
          },
        },
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            const id = row["RULE"];
            const rule = rules.find((rule) => rule.id === id);
            if (rule) {
              setDeleteSelected([rule]);
              setShowDeleteModal(true);
            }
          },
          hide: (row) => {
            const id = row["RULE"];
            const rule = rules.find((rule) => rule.id === id);
            return isDefaultRule(row["POLICY"], rule, defaultPolicy);
          },
        },
      ],
    },
  ];

  return (
    <>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={rows}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="configuration-rules"
          rowsPerPageNr={50}
          selected={selected}
          setSelected={setSelected}
          deleteSelected={(values) => {
            const selectedItems = rows
              .filter((row) => values.includes(row.id))
              .map((row) => rules.find((r) => r.id === row["RULE"]));
            setDeleteSelected(selectedItems);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        view={view}
        whenDone={() => {
          setSelected([]);
          actions.send("do-load");
        }}
        defaultPolicy={defaultPolicy}
      />
    </>
  );
};

export default RulesTable;
